import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { 
  Bell, BellOff, Clock, Trophy, Flame, Calendar,
  CheckCircle, AlertCircle, Loader2, Settings, ChevronDown, ChevronUp
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationManager = ({ compact = false }) => {
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [status, setStatus] = useState({ subscribed: false, preferences: {} });
  const [showPreferences, setShowPreferences] = useState(false);
  const [minimized, setMinimized] = useState(() => {
    return localStorage.getItem('notification_manager_minimized') === 'true';
  });
  const [preferences, setPreferences] = useState({
    study_reminders: true,
    reminder_time: '19:00',
    streak_alerts: true,
    achievement_alerts: true,
    weekly_summary: true,
    exam_reminders: true
  });

  const toggleMinimized = () => {
    const newState = !minimized;
    setMinimized(newState);
    localStorage.setItem('notification_manager_minimized', String(newState));
  };

  useEffect(() => {
    checkNotificationStatus();
  }, []);

  const checkNotificationStatus = async () => {
    try {
      const token = localStorage.getItem('session_token');
      if (!token) return;

      const response = await axios.get(`${API}/notifications/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setStatus(response.data);
      if (response.data.preferences) {
        setPreferences(response.data.preferences);
      }
    } catch (error) {
      console.error('Error checking notification status:', error);
    } finally {
      setLoading(false);
    }
  };

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('Tu navegador no soporta notificaciones push');
      return false;
    }

    if (Notification.permission === 'denied') {
      toast.error('Las notificaciones están bloqueadas. Habilítalas en la configuración del navegador.');
      return false;
    }

    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Necesitas permitir las notificaciones para activar los recordatorios');
        return false;
      }
    }

    return true;
  };

  const registerServiceWorker = async () => {
    if (!('serviceWorker' in navigator)) {
      console.warn('Service workers not supported');
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service worker registered:', registration);
      return registration;
    } catch (error) {
      console.error('Service worker registration failed:', error);
      return null;
    }
  };

  const subscribeToNotifications = async () => {
    setSubscribing(true);
    
    try {
      // Request permission
      const hasPermission = await requestNotificationPermission();
      if (!hasPermission) {
        setSubscribing(false);
        return;
      }

      // Register service worker
      const registration = await registerServiceWorker();
      if (!registration) {
        toast.error('Error registrando el service worker');
        setSubscribing(false);
        return;
      }

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      // Get VAPID public key
      const vapidResponse = await axios.get(`${API}/notifications/vapid-key`);
      const vapidPublicKey = vapidResponse.data.publicKey;

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to backend
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/notifications/subscribe`, {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
          auth: arrayBufferToBase64(subscription.getKey('auth'))
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Also register for FCM if available (for Firebase push)
      try {
        await registerFCMToken(token);
      } catch (fcmError) {
        console.warn('FCM registration failed (non-critical):', fcmError);
      }

      toast.success('¡Notificaciones activadas! Recibirás recordatorios de estudio.');
      setStatus(prev => ({ ...prev, subscribed: true }));
      
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Error al activar notificaciones');
    } finally {
      setSubscribing(false);
    }
  };

  // Register FCM token with backend for Firebase Cloud Messaging
  const registerFCMToken = async (authToken) => {
    try {
      // Get device info for timezone-aware notifications
      const deviceInfo = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height
      };

      // For now, we use a placeholder token until Firebase is fully configured
      // In production, this would be the actual FCM token from firebase.getToken()
      const fcmToken = `web_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      await axios.post(`${API}/notifications/register-fcm-token`, {
        token: fcmToken,
        device_info: deviceInfo
      }, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      console.log('FCM token registered successfully');
    } catch (error) {
      console.error('FCM registration error:', error);
      throw error;
    }
  };

  const unsubscribeFromNotifications = async () => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/notifications/unsubscribe`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Unsubscribe from push manager
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
        }
      }

      toast.success('Notificaciones desactivadas');
      setStatus(prev => ({ ...prev, subscribed: false }));
    } catch (error) {
      console.error('Unsubscribe error:', error);
      toast.error('Error al desactivar notificaciones');
    }
  };

  const updatePreferences = async (newPrefs) => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.put(`${API}/notifications/preferences`, newPrefs, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPreferences(newPrefs);
      toast.success('Preferencias actualizadas');
    } catch (error) {
      toast.error('Error al guardar preferencias');
    }
  };

  const handlePreferenceChange = (key, value) => {
    const newPrefs = { ...preferences, [key]: value };
    setPreferences(newPrefs);
    updatePreferences(newPrefs);
  };

  // Helper functions
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">Cargando...</span>
      </div>
    );
  }

  // Compact mode for header/navbar
  if (compact) {
    return (
      <button
        onClick={status.subscribed ? () => setShowPreferences(true) : subscribeToNotifications}
        disabled={subscribing}
        className={`p-2 rounded-full transition-colors ${
          status.subscribed 
            ? 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200' 
            : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
        }`}
        title={status.subscribed ? 'Configurar notificaciones' : 'Activar notificaciones'}
      >
        {subscribing ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : status.subscribed ? (
          <Bell className="w-5 h-5" />
        ) : (
          <BellOff className="w-5 h-5" />
        )}
      </button>
    );
  }

  // Full card mode
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden transition-all duration-300" data-testid="notification-manager">
      <div 
        className={`flex items-center justify-between p-6 ${minimized ? 'cursor-pointer hover:bg-slate-50' : ''}`}
        onClick={minimized ? toggleMinimized : undefined}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            status.subscribed ? 'bg-indigo-100' : 'bg-slate-100'
          }`}>
            {status.subscribed ? (
              <Bell className="w-5 h-5 text-indigo-600" />
            ) : (
              <BellOff className="w-5 h-5 text-slate-400" />
            )}
          </div>
          <div>
            <h3 className="font-bold text-slate-900">Recordatorios de Estudio</h3>
            {!minimized && (
              <p className="text-sm text-slate-500">
                {status.subscribed 
                  ? 'Recibirás notificaciones para mantener tu racha'
                  : 'Activa las notificaciones para no olvidar estudiar'}
              </p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {!minimized && (
            <Button
              onClick={status.subscribed ? unsubscribeFromNotifications : subscribeToNotifications}
              disabled={subscribing}
              variant={status.subscribed ? "outline" : "default"}
              className={!status.subscribed ? "bg-indigo-600 hover:bg-indigo-700" : ""}
            >
              {subscribing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Activando...
                </>
              ) : status.subscribed ? (
                'Desactivar'
              ) : (
                <>
                  <Bell className="w-4 h-4 mr-2" />
                  Activar
                </>
              )}
            </Button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); toggleMinimized(); }}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            title={minimized ? 'Expandir' : 'Minimizar'}
          >
            {minimized ? (
              <ChevronDown className="w-5 h-5" />
            ) : (
              <ChevronUp className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {!minimized && status.subscribed && (
        <div className="px-6 pb-6 pt-0 border-t border-slate-100">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-slate-900 flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Preferencias
            </h4>
          </div>

          <div className="space-y-4">
            {/* Study Reminders */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-indigo-500" />
                <div>
                  <p className="font-medium text-slate-900">Recordatorios diarios</p>
                  <p className="text-xs text-slate-500">Te avisamos si no has estudiado</p>
                </div>
              </div>
              <Switch
                checked={preferences.study_reminders}
                onCheckedChange={(checked) => handlePreferenceChange('study_reminders', checked)}
              />
            </div>

            {/* Reminder Time */}
            {preferences.study_reminders && (
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg ml-4">
                <div>
                  <p className="text-sm font-medium text-slate-700">Hora del recordatorio</p>
                </div>
                <input
                  type="time"
                  value={preferences.reminder_time}
                  onChange={(e) => handlePreferenceChange('reminder_time', e.target.value)}
                  className="px-3 py-1 rounded border border-slate-200 text-sm"
                />
              </div>
            )}

            {/* Streak Alerts */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Flame className="w-5 h-5 text-orange-500" />
                <div>
                  <p className="font-medium text-slate-900">Alertas de racha</p>
                  <p className="text-xs text-slate-500">Aviso cuando tu racha está en peligro</p>
                </div>
              </div>
              <Switch
                checked={preferences.streak_alerts}
                onCheckedChange={(checked) => handlePreferenceChange('streak_alerts', checked)}
              />
            </div>

            {/* Achievement Alerts */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Trophy className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="font-medium text-slate-900">Logros desbloqueados</p>
                  <p className="text-xs text-slate-500">Celebra tus nuevos logros</p>
                </div>
              </div>
              <Switch
                checked={preferences.achievement_alerts}
                onCheckedChange={(checked) => handlePreferenceChange('achievement_alerts', checked)}
              />
            </div>

            {/* Weekly Summary */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-emerald-500" />
                <div>
                  <p className="font-medium text-slate-900">Resumen semanal</p>
                  <p className="text-xs text-slate-500">Tu progreso de la semana</p>
                </div>
              </div>
              <Switch
                checked={preferences.weekly_summary}
                onCheckedChange={(checked) => handlePreferenceChange('weekly_summary', checked)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Status indicator - show when subscribed and minimized */}
      {minimized && status.subscribed && (
        <div className="pb-4 px-6 flex items-center gap-2 text-sm text-emerald-600">
          <CheckCircle className="w-4 h-4" />
          <span>Notificaciones activas</span>
        </div>
      )}
    </div>
  );
};

export default NotificationManager;
